import { useRequiredContext } from "@redotech/react-util/context";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { memo } from "react";
import { matchPath, Navigate, Outlet } from "react-router-dom";
import { useDisputeAlertsEnrollmentEnabled } from "../../../payments/chargebacks/use-dispute-alerts-enrollment-enabled";
import { useDisputeResolveEnabled } from "../../../payments/chargebacks/use-dispute-resolve-enabled";
import { RedoEmployeeGate } from "./redo-employee-gate";

const disputeDetailBreadcrumb = breadcrumbSlot();

const HIDE_PADDING_ROUTES: string[] = [
  "/chargebacks/overview",
  "/chargebacks/reclaim",
  "/chargebacks/reclaim/*",
  "/chargebacks/alerts",
  "/chargebacks/alerts/*",
  "/chargebacks/resolve",
  "/chargebacks/resolve/*",
  "/chargebacks/:disputeId",
  "/hsa/questionnaire",
  "/hsa/questionnaire/edit",
];

//redontIgnore -- shared hide-padding shape every route file repeats by convention
function hidePadding(url: string): boolean {
  return HIDE_PADDING_ROUTES.some(
    (route) => !!matchPath(`/stores/:storeId/payments${route}`, url),
  );
}

/** HSA-only merchants land on the HSA dashboard instead of the Reclaim upsell. */
export function paymentsDefaultPath(settings: {
  disputes?: { enabled?: boolean };
  hsa?: { enabled?: boolean };
}): "chargebacks" | "hsa" {
  if (!settings.disputes?.enabled && settings.hsa?.enabled) {
    return "hsa";
  }
  return "chargebacks";
}

/**
 * A cross-product Overview only earns its place once there is more than one
 * product. A merchant running Reclaim alone lands on Reclaim, whose own Overview
 * tab is the dashboard they want.
 */
export function chargebacksDefaultPath(products: {
  alertsEnabled: boolean;
  resolveEnabled: boolean;
}): "overview" | "reclaim" {
  return products.alertsEnabled || products.resolveEnabled
    ? "overview"
    : "reclaim";
}

const ChargebacksDefaultRoute = memo(function ChargebacksDefaultRoute() {
  const { pending: alertsPending, enabled: alertsEnabled } =
    useDisputeAlertsEnrollmentEnabled();
  const { pending: resolvePending, enabled: resolveEnabled } =
    useDisputeResolveEnabled();
  if (alertsPending || resolvePending) {
    return null;
  }
  return (
    <Navigate
      replace
      to={chargebacksDefaultPath({ alertsEnabled, resolveEnabled })}
    />
  );
});

const PaymentsDefaultRoute = memo(function PaymentsDefaultRoute() {
  const team = useRequiredContext(TeamContext);

  return <Navigate replace to={paymentsDefaultPath(team.settings)} />;
});

const HsaEnabledGate = memo(function HsaEnabledGate() {
  const team = useRequiredContext(TeamContext);

  if (!team.settings.hsa?.enabled) {
    return <Navigate replace to="../chargebacks" />;
  }
  return <Outlet />;
});

export const paymentsRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Payments" },
    async lazy() {
      const { Page } = await import("../../page");
      return { element: <Page hidePadding={hidePadding} /> };
    },
    children: [
      { index: true, element: <PaymentsDefaultRoute /> },
      {
        path: "hsa",
        handle: { breadcrumb: "HSA/FSA payments" },
        element: <HsaEnabledGate />,
        children: [
          {
            index: true,
            async lazy() {
              const { HsaAnalyticsPage } =
                await import("../../../payments/hsa/hsa-analytics-page");
              return { Component: HsaAnalyticsPage };
            },
          },
          {
            path: "questionnaire",
            handle: { breadcrumb: "Questionnaire" },
            element: <RedoEmployeeGate />,
            children: [
              {
                index: true,
                async lazy() {
                  const { HsaQuestionnaireFlowViewPage } =
                    await import("../../../payments/hsa/hsa-questionnaire-flow-view-page");
                  return { Component: HsaQuestionnaireFlowViewPage };
                },
              },
              {
                path: "edit",
                handle: { breadcrumb: "Edit" },
                async lazy() {
                  const { HsaQuestionnaireFlowEditPage } =
                    await import("../../../payments/hsa/hsa-questionnaire-flow-edit-page");
                  return { Component: HsaQuestionnaireFlowEditPage };
                },
              },
            ],
          },
          {
            path: "transactions",
            handle: { breadcrumb: "Transactions" },
            async lazy() {
              const { HsaTransactionsPage } =
                await import("../../../payments/hsa/hsa-transactions-page");
              return { Component: HsaTransactionsPage };
            },
          },
          {
            path: "payouts",
            handle: { breadcrumb: "Payouts" },
            children: [
              {
                index: true,
                async lazy() {
                  const { HsaPayoutsPage } =
                    await import("../../../payments/hsa/hsa-payouts-page");
                  return { Component: HsaPayoutsPage };
                },
              },
              {
                path: ":payoutId",
                handle: { breadcrumb: "Payout" },
                async lazy() {
                  const { HsaPayoutDetailPage } =
                    await import("../../../payments/hsa/hsa-payout-detail-page");
                  return { Component: HsaPayoutDetailPage };
                },
              },
            ],
          },
        ],
      },
      {
        // Redo-owned Adyen onboarding (HSA-150); no navigation entry while dark.
        path: "kyc",
        handle: { breadcrumb: "Verify your business" },
        async lazy() {
          const { HsaAdyenKycPage } =
            await import("../../../payments/kyc/hsa-adyen-kyc-page");
          return { Component: HsaAdyenKycPage };
        },
      },
      {
        path: "chargebacks",
        handle: { breadcrumb: "Chargebacks" },
        async lazy() {
          const { DisputesEnabledGate } =
            await import("../../../payments/chargebacks/disputes-enabled-gate");
          return {
            element: (
              <DisputesEnabledGate>
                <Outlet />
              </DisputesEnabledGate>
            ),
          };
        },
        children: [
          { index: true, element: <ChargebacksDefaultRoute /> },
          {
            path: "overview",
            handle: { breadcrumb: "Overview" },
            async lazy() {
              const { ChargebacksOverviewPage } =
                await import("../../../payments/chargebacks/overview/chargebacks-overview-page");
              return { Component: ChargebacksOverviewPage };
            },
          },
          {
            path: "reclaim",
            handle: { breadcrumb: "Reclaim" },
            children: [
              {
                index: true,
                async lazy() {
                  const { DisputesPage } =
                    await import("../../../payments/chargebacks/disputes-page");
                  return { Component: DisputesPage };
                },
              },
              {
                path: ":disputeId",
                handle: { breadcrumb: disputeDetailBreadcrumb },
                async lazy() {
                  const { DisputeDetailPage } =
                    await import("../../../payments/chargebacks/dispute-detail-page");
                  return {
                    element: (
                      <DisputeDetailPage breadcrumb={disputeDetailBreadcrumb} />
                    ),
                  };
                },
              },
            ],
          },
          {
            path: "alerts",
            handle: { breadcrumb: "Alerts" },
            children: [
              {
                index: true,
                async lazy() {
                  const { DisputeAlertsPage } =
                    await import("../../../payments/chargebacks/alerts/dispute-alerts-page");
                  return { Component: DisputeAlertsPage };
                },
              },
              {
                path: ":alertId",
                handle: { breadcrumb: "Alert details" },
                async lazy() {
                  const { AlertDetailPage } =
                    await import("../../../payments/chargebacks/alerts/alert-detail-page");
                  return { Component: AlertDetailPage };
                },
              },
            ],
          },
          {
            path: "resolve",
            handle: { breadcrumb: "Resolve" },
            children: [
              {
                index: true,
                async lazy() {
                  const { ResolveDeflectionsPage } =
                    await import("../../../payments/chargebacks/resolve/resolve-deflections-page");
                  return { Component: ResolveDeflectionsPage };
                },
              },
              {
                path: ":deflectionId",
                handle: { breadcrumb: "Resolve request" },
                async lazy() {
                  const { ResolveDeflectionDetailPage } =
                    await import("../../../payments/chargebacks/resolve/resolve-deflection-detail-page");
                  return { Component: ResolveDeflectionDetailPage };
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
